import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Ticket, Download, QrCode, Calendar, MapPin, RefreshCw, ArrowUpRight, Search } from 'lucide-react';
import { ticketsAPI } from '../lib/api';
import { API } from '../lib/api';
import QRCode from 'react-qr-code';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { toast } from 'sonner';

const STATUS_BADGE = {
  false: 'badge-paid',
  true: 'badge-cancelled',
};

export default function MyTicketsPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { format } = useCurrency();
  const [tickets, setTickets] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('upcoming');
  const [loading, setLoading] = useState(true);
  const [selectedQR, setSelectedQR] = useState(null);
  const [swapModal, setSwapModal] = useState(null);
  const [swapForm, setSwapForm] = useState({ new_holder_email: '', new_holder_name: '' });

  useEffect(() => {
    ticketsAPI.myTickets()
      .then(r => { setTickets(r.data.tickets || []); setFiltered(r.data.tickets || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const q = search.toLowerCase();
    const now = Date.now();
    setFiltered(tickets.filter(t => {
      const d = new Date(t.event_date).getTime();
      const isPast = !isNaN(d) && d < now;
      if (tab === 'past' ? !isPast : isPast) return false;
      return !q ||
        t.event_title?.toLowerCase().includes(q) ||
        t.event_location?.toLowerCase().includes(q) ||
        t.ticket_id?.toLowerCase().includes(q);
    }));
  }, [search, tickets, tab]);

  const handleSecureSwap = async (ticketId) => {
    try {
      await ticketsAPI.secureSwap(ticketId, swapForm);
      toast.success('Ticket transferred via SecureSwap! New QR code generated.');
      setSwapModal(null);
      const resp = await ticketsAPI.myTickets();
      setTickets(resp.data.tickets || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Transfer failed');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#050505] pt-16 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">Please sign in to view your tickets</p>
          <Link to="/auth" className="btn-primary px-6 py-3 rounded text-sm font-bold">Sign In</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-16 page-enter" data-testid="my-tickets-page">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-black uppercase tracking-tight text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {t('myTicketsTitle')}
          </h1>
          <Link to="/explore" className="btn-primary text-xs px-4 py-2 rounded flex items-center gap-1">
            Explore <ArrowUpRight size={12} />
          </Link>
        </div>

        {/* Upcoming / Past tabs */}
        <div className="flex border-b border-gray-800 mb-6">
          {[{ id: 'upcoming', label: 'Upcoming' }, { id: 'past', label: 'Past bookings' }].map(tb => (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              className={`px-1 mr-8 pb-3 text-sm font-bold uppercase tracking-wide border-b-2 transition-colors ${
                tab === tb.id ? 'text-white border-white' : 'text-gray-500 border-transparent hover:text-gray-300'
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        {/* Search filter */}
        <div className="relative mb-6">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by event name, location, ticket ID..."
            className="w-full bg-[#0A0A0A] border border-gray-800 text-white pl-9 pr-4 py-2.5 rounded-lg text-sm focus:border-red-500 outline-none"
            data-testid="tickets-search-input"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
              ×
            </button>
          )}
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="skeleton h-40 rounded-xl" />
            ))}
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-20">
            <Ticket className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <p className="text-gray-500 text-lg font-medium mb-2">No tickets yet</p>
            <p className="text-gray-600 text-sm mb-6">Start exploring events and buy your first ticket</p>
            <Link to="/explore" className="btn-primary px-6 py-3 rounded text-sm font-bold">Explore Events</Link>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 text-sm">No tickets match "{search}"</p>
            <button onClick={() => setSearch('')} className="text-red-400 text-xs mt-2 hover:text-red-300">Clear search</button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="tickets-list">
            {filtered.map(ticket => (
              <div key={ticket.ticket_id} className="ticket-card p-5 md:p-6" data-testid={`ticket-${ticket.ticket_id}`}>
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div>
                        <p className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-1">{ticket.ticket_type_name}</p>
                        <h3 className="text-white font-black uppercase text-base md:text-lg leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                          {ticket.event_title}
                        </h3>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase flex-shrink-0 ${ticket.is_used ? 'badge-cancelled' : 'badge-paid'}`}>
                        {ticket.is_used ? 'Used' : 'Valid'}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-3 text-gray-400 text-xs mb-3">
                      <span className="flex items-center gap-1"><Calendar size={11} /> {ticket.event_date}</span>
                      <span className="flex items-center gap-1"><MapPin size={11} /> {ticket.event_location}</span>
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <span className="font-mono">#{ticket.ticket_id.slice(0, 8).toUpperCase()}</span>
                      <span>{format(ticket.price, ticket.currency)}</span>
                    </div>

                    {ticket.is_resold && (
                      <span className="text-xs text-yellow-400/70 mt-1 block">Transferred via SecureSwap</span>
                    )}
                  </div>

                  {/* QR code area */}
                  {selectedQR === ticket.ticket_id ? (
                    <div className="flex flex-col items-center gap-2 bg-white p-3 rounded-lg">
                      <QRCode value={ticket.qr_data} size={120} />
                      <button onClick={() => setSelectedQR(null)} className="text-gray-500 text-xs hover:text-gray-700">Hide</button>
                    </div>
                  ) : (
                    <div className="flex md:flex-col gap-2">
                      <button
                        onClick={() => setSelectedQR(ticket.ticket_id)}
                        className="flex-1 md:flex-none flex items-center justify-center gap-1.5 py-2 px-3 border border-gray-700 rounded text-xs font-bold uppercase text-gray-300 hover:border-red-500 hover:text-white transition-colors"
                        data-testid={`view-qr-${ticket.ticket_id}`}
                      >
                        <QrCode size={12} /> QR
                      </button>
                      <a
                        href={`${API}/tickets/${ticket.ticket_id}/pdf`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex-1 md:flex-none flex items-center justify-center gap-1.5 py-2 px-3 btn-primary rounded text-xs font-bold uppercase"
                        data-testid={`download-${ticket.ticket_id}`}
                      >
                        <Download size={12} /> PDF
                      </a>
                      {!ticket.is_used && (
                        <button
                          onClick={() => { setSwapModal(ticket.ticket_id); setSwapForm({ new_holder_email: '', new_holder_name: '' }); }}
                          className="flex-1 md:flex-none flex items-center justify-center gap-1.5 py-2 px-3 border border-yellow-500/30 rounded text-xs font-bold uppercase text-yellow-400 hover:bg-yellow-400/10 transition-colors"
                          data-testid={`swap-${ticket.ticket_id}`}
                        >
                          <RefreshCw size={12} /> Transfer
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* SecureSwap modal */}
      {swapModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="swap-modal">
          <div className="bg-[#0A0A0A] border border-gray-700 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-white font-black uppercase text-lg mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>SecureSwap Transfer</h3>
            <p className="text-gray-500 text-xs mb-6">Transfer this ticket to someone else. The old QR code will be invalidated and a new one generated.</p>
            <div className="space-y-4">
              <div>
                <label className="text-gray-400 text-xs font-medium uppercase mb-1.5 block">New Holder Name</label>
                <input
                  type="text"
                  value={swapForm.new_holder_name}
                  onChange={e => setSwapForm(f => ({ ...f, new_holder_name: e.target.value }))}
                  className="w-full bg-[#111] border border-gray-700 text-white px-3 py-2.5 rounded text-sm"
                  data-testid="swap-name-input"
                />
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium uppercase mb-1.5 block">New Holder Email</label>
                <input
                  type="email"
                  value={swapForm.new_holder_email}
                  onChange={e => setSwapForm(f => ({ ...f, new_holder_email: e.target.value }))}
                  className="w-full bg-[#111] border border-gray-700 text-white px-3 py-2.5 rounded text-sm"
                  data-testid="swap-email-input"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setSwapModal(null)} className="flex-1 py-2.5 border border-gray-700 rounded text-sm text-gray-400 hover:text-white">Cancel</button>
              <button onClick={() => handleSecureSwap(swapModal)} className="flex-1 btn-primary py-2.5 rounded text-sm font-bold">Transfer Ticket</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
